import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getImageUrl } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Building2, Camera, Save, Loader2, ImagePlus } from 'lucide-react';

const CompanyProfilePage = () => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/companies/my-profile`)
      .then(r => setCompany(r.data))
      .catch(() => toast.error('Erro ao carregar perfil'))
      .finally(() => setLoading(false));
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx 5MB)');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/companies/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCompany(prev => ({ ...prev, logo: res.data.logo }));
      toast.success('Logo atualizado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar logo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!company) return <div className="text-center py-12 text-slate-500">Perfil não encontrado</div>;

  return (
    <div className="space-y-6" data-testid="company-profile-section">
      <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Perfil da Empresa</h2>

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="relative group">
              {company.logo ? (
                <img
                  src={getImageUrl(company.logo)}
                  alt="Logo da empresa"
                  className="w-24 h-24 rounded-2xl object-cover border-4 border-slate-200"
                  data-testid="company-logo-img"
                />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-slate-100 flex items-center justify-center border-4 border-slate-50" data-testid="company-logo-placeholder">
                  <Building2 className="w-10 h-10 text-slate-400" />
                </div>
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-testid="company-logo-upload-label">
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-white" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} data-testid="company-logo-upload-input" />
              </label>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">{company.name}</h3>
              {company.cnpj && <p className="text-sm text-slate-500">CNPJ: {company.cnpj}</p>}
              {company.city && <p className="text-sm text-slate-400">{company.city}{company.state ? `, ${company.state}` : ''}</p>}
              <p className="text-xs text-slate-400 mt-1">Passe o mouse no ícone para trocar o logo</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Dados da Empresa</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500 text-xs">Nome</Label>
              <p className="text-sm font-medium text-slate-900">{company.name}</p>
            </div>
            <div>
              <Label className="text-slate-500 text-xs">CNPJ</Label>
              <p className="text-sm font-medium text-slate-900">{company.cnpj || '—'}</p>
            </div>
            <div>
              <Label className="text-slate-500 text-xs">E-mail</Label>
              <p className="text-sm font-medium text-slate-900">{company.email || '—'}</p>
            </div>
            <div>
              <Label className="text-slate-500 text-xs">Telefone</Label>
              <p className="text-sm font-medium text-slate-900">{company.phone || '—'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main RH Dashboard Component

export default CompanyProfilePage;
